import { useEffect, useMemo, useState } from "react";
import { X, RefreshCw, Send, Mail, MessageSquare } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { RecipientPicker } from "./RecipientPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

/**
 * Unified compose modal — handles a fresh "+ New Email", "+ New SMS", or a
 * "Duplicate as new send" from an existing communications row.
 *
 * Props:
 *   channel: "email" | "sms"          (required)
 *   seed?: {
 *     subject, body, to_email, to_phone,
 *     recipient_name, customer_id, booking_id, trial_lead_id, appointment_id,
 *     template_name, keep_recipient_default,
 *   }                                 (optional — pre-fill from a duplicated row)
 *   templates?: Array<{key,name,subject?,body}>   (optional — applied via the dropdown)
 *   defaultTemplateKey?: string       (optional — default template_key sent to backend)
 *   title?: string                    (optional — modal title override)
 *   onClose: () => void
 *   onSent: () => void
 */
export function ComposeMessageModal({
  channel,
  seed = null,
  templates = [],
  defaultTemplateKey = "",
  title,
  onClose,
  onSent,
}) {
  const isEmail = channel === "email";
  const isSms = channel === "sms";

  const [subject, setSubject] = useState(seed?.subject || "");
  const [body, setBody] = useState(seed?.body || "");
  const [toEmail, setToEmail] = useState(seed?.to_email || "");
  const [toPhone, setToPhone] = useState(seed?.to_phone || "");
  const [recipientName, setRecipientName] = useState(seed?.recipient_name || "");
  // When duplicating from an existing row, default the "Keep original recipient"
  // checkbox state to whatever the backend recommended.
  const [keepRecipient, setKeepRecipient] = useState(seed?.keep_recipient_default ?? Boolean(seed?.to_email || seed?.to_phone));
  const [templateKey, setTemplateKey] = useState(defaultTemplateKey || seed?.template_name || "");
  const [sending, setSending] = useState(false);

  // Section 7 (Recipient Selector) — a picked recipient auto-populates
  // to_email / to_phone / recipient_name AND locks in the linked entity IDs
  // (customer_id / booking_id / trial_lead_id / appointment_id) so the sent
  // comm joins to the source record in the DB.
  const [pickedRecipient, setPickedRecipient] = useState(() => {
    // If we're seeded from a Duplicate flow that already has a linkage, seed the
    // picker as "selected" so the admin sees the same linkage and can clear it.
    if (!seed) return null;
    const anyLink = seed.customer_id || seed.booking_id || seed.trial_lead_id || seed.appointment_id;
    if (!anyLink) return null;
    const type = seed.appointment_id || seed.trial_lead_id ? "trial" : seed.booking_id ? "booking" : "customer";
    return {
      type,
      id: seed.appointment_id || seed.trial_lead_id || seed.booking_id || seed.customer_id,
      label: seed.recipient_name || seed.to_email || seed.to_phone || "Linked recipient",
      email: seed.to_email || "",
      phone: seed.to_phone || "",
      customer_id: seed.customer_id || "",
      booking_id: seed.booking_id || "",
      trial_lead_id: seed.trial_lead_id || "",
      appointment_id: seed.appointment_id || "",
      meta: "",
    };
  });

  // If admin unticks "Keep original recipient", blank out the recipient fields
  // so they're forced to type a new one. If they re-tick, restore the seed values.
  useEffect(() => {
    if (!seed) return;
    if (!keepRecipient) {
      setToEmail("");
      setToPhone("");
      setRecipientName("");
    } else {
      setToEmail(seed.to_email || "");
      setToPhone(seed.to_phone || "");
      setRecipientName(seed.recipient_name || "");
    }
  }, [keepRecipient, seed]);

  const linkedIds = useMemo(() => {
    // A live pick from the Recipient Selector always wins over the seed values —
    // when admin picks a recipient we should link the new comm to that entity.
    if (pickedRecipient) {
      return {
        customer_id: pickedRecipient.customer_id || "",
        booking_id: pickedRecipient.booking_id || "",
        trial_lead_id: pickedRecipient.trial_lead_id || "",
        appointment_id: pickedRecipient.appointment_id || "",
      };
    }
    return {
      customer_id: keepRecipient ? (seed?.customer_id || "") : "",
      booking_id: keepRecipient ? (seed?.booking_id || "") : "",
      trial_lead_id: keepRecipient ? (seed?.trial_lead_id || "") : "",
      appointment_id: keepRecipient ? (seed?.appointment_id || "") : "",
    };
  }, [keepRecipient, seed, pickedRecipient]);

  const handlePickRecipient = (r) => {
    setPickedRecipient(r);
    if (isEmail) {
      setToEmail(r.email || "");
    } else {
      setToPhone(r.phone || "");
    }
    setRecipientName(r.label?.split(" · ")[0] || r.email || r.phone || "");
    // A fresh pick supersedes any "keep original recipient" seed state.
    setKeepRecipient(false);
  };

  const handleClearRecipient = () => {
    setPickedRecipient(null);
    // Only clear the fields if the admin hasn't further edited them past the
    // pick — but the safest UX is to fully clear so no stale linkage remains.
    if (isEmail) setToEmail("");
    else setToPhone("");
    setRecipientName("");
  };

  // ── Address-change guard ─────────────────────────────────────────────────
  // When admin manually edits the To email/phone AFTER picking a recipient,
  // the linkage to that Customer/Booking/Trial is no longer trustworthy —
  // silently keeping it attached to a different address would misroute audit
  // trails. Auto-clear the pick AND warn once per edit.
  const _addrsMatch = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

  const handleToEmailChange = (nextValue) => {
    setToEmail(nextValue);
    if (pickedRecipient && !_addrsMatch(nextValue, pickedRecipient.email)) {
      setPickedRecipient(null);
      setRecipientName("");
      toast.warning("Recipient details were changed. The customer and booking links have been cleared.");
    }
  };
  const handleToPhoneChange = (nextValue) => {
    setToPhone(nextValue);
    if (pickedRecipient && !_addrsMatch(nextValue, pickedRecipient.phone)) {
      setPickedRecipient(null);
      setRecipientName("");
      toast.warning("Recipient details were changed. The customer and booking links have been cleared.");
    }
  };

  const applyTemplate = (key) => {
    setTemplateKey(key);
    const tpl = templates.find(t => t.key === key);
    if (!tpl) return;
    if (isEmail) {
      setSubject(tpl.subject || subject);
      setBody(tpl.body || body);
    } else {
      setBody(tpl.body || body);
    }
  };

  const send = async () => {
    if (sending) return;                                  // Section 19 — prevent duplicate sends from repeated taps
    if (isEmail && !subject.trim()) { toast.error("Subject is required"); return; }
    if (!body.trim()) { toast.error("Message body is required"); return; }
    if (isEmail && !toEmail.trim()) { toast.error("Recipient email is required"); return; }
    if (isSms && !toPhone.trim()) { toast.error("Recipient phone is required"); return; }
    setSending(true);
    try {
      const baseLinked = {
        recipient_name: recipientName,
        ...linkedIds,
      };
      if (isEmail) {
        const payload = {
          ...baseLinked,
          to_email: toEmail.trim(),
          subject: subject.trim(),
          body: body,
          template_key: templateKey || "admin_manual_compose",
          template_id: "",
        };
        const res = await axios.post(`${API}/admin/follow-up/email/send`, payload, { headers: auth() });
        if (res.data?.ok) {
          toast.success("Email sent");
          onSent?.();
        } else {
          toast.error("Email queued but provider reported failure — check Communications log");
          onSent?.();
        }
      } else {
        const payload = {
          ...baseLinked,
          to_phone: toPhone.trim(),
          body: body.trim(),
          template_key: templateKey || "admin_manual_compose_sms",
        };
        const res = await axios.post(`${API}/admin/follow-up/sms/send`, payload, { headers: auth() });
        if (res.data?.ok) {
          toast.success("SMS sent");
          onSent?.();
        } else {
          toast.error(res.data?.error || "Send failed — see Communications log");
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  const Icon = isEmail ? Mail : MessageSquare;
  const resolvedTitle = title || (seed
    ? `Duplicate as new ${isEmail ? "email" : "SMS"}`
    : `Compose ${isEmail ? "Email" : "SMS"}`);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-xl sm:w-auto rounded-t-2xl sm:rounded-none max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="compose-message-modal"
      >
        {/* Sticky header — safe from keyboard on mobile */}
        <div className="sticky top-0 z-10 flex justify-between items-start px-4 sm:px-5 pt-4 sm:pt-5 pb-3 bg-white border-b border-[#282828]/10">
          <div className="flex items-center gap-2.5">
            <Icon className="w-5 h-5 text-[#9A7B4F]" />
            <div>
              <h3 className="text-[16px] font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{resolvedTitle}</h3>
              <p className="text-[10px] text-[#676664]/60 mt-0.5 uppercase tracking-wider">{isEmail ? "Branded Mahra email" : "ClickSend SMS"}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#676664] p-1 -m-1" data-testid="compose-close" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4">

        {/* Section 7 — Recipient Selector (autocomplete across Customer/Booking/Trial) */}
        <div className="mb-3">
          <RecipientPicker
            channel={channel}
            selected={pickedRecipient}
            onSelect={handlePickRecipient}
            onClear={handleClearRecipient}
            placeholder={isEmail ? "Search a customer, booking or trial…" : "Search a customer, booking or trial…"}
          />
        </div>

        {seed && (seed.to_email || seed.to_phone) && (
          <label className="flex items-center gap-2 mb-3 px-3 py-2 bg-[#FAF8F0] border border-[#9A7B4F]/20 cursor-pointer" data-testid="keep-recipient-wrap">
            <input
              type="checkbox"
              checked={keepRecipient}
              onChange={e => setKeepRecipient(e.target.checked)}
              className="accent-[#9A7B4F]"
              data-testid="keep-recipient-checkbox"
            />
            <span className="text-[12px] text-[#3D2B1F]">
              Keep original recipient
              <span className="text-[10px] text-[#676664]/70 ml-1">({isEmail ? (seed.to_email || "—") : (seed.to_phone || "—")}{seed.recipient_name ? ` · ${seed.recipient_name}` : ""})</span>
            </span>
          </label>
        )}

        {templates.length > 0 && (
          <div className="mb-3">
            <label className="block text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1">Template</label>
            <select
              value={templateKey}
              onChange={e => applyTemplate(e.target.value)}
              className="w-full border border-[#282828]/12 px-3 py-2 text-[12px] bg-white"
              data-testid="compose-template-select"
            >
              <option value="">— Blank / custom —</option>
              {templates.map(t => (
                <option key={t.key} value={t.key}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          {isEmail ? (
            <>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1">To (email)</label>
                <input type="email" value={toEmail} onChange={e => handleToEmailChange(e.target.value)} placeholder="customer@example.com"
                  className="w-full border border-[#282828]/12 px-3 py-2 text-[13px]" data-testid="compose-to-email" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1">Recipient name (optional)</label>
                <input value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="Olivia"
                  className="w-full border border-[#282828]/12 px-3 py-2 text-[13px]" data-testid="compose-recipient-name" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1">Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject"
                  className="w-full border border-[#282828]/12 px-3 py-2 text-[13px]" data-testid="compose-subject" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1">
                  Body
                  <span className="ml-2 normal-case text-[#676664]/50 tracking-normal">Tokens: [Customer Name], [Contact Number], [Booking ID], [Trial Lead ID]</span>
                </label>
                <textarea rows={9} value={body} onChange={e => setBody(e.target.value)} placeholder="Hi [Customer Name],&#10;&#10;Type your message here…&#10;&#10;Kind regards,&#10;Mahra"
                  className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] resize-y font-mono" data-testid="compose-body" />
                <p className="text-[10px] text-[#676664]/50 mt-1">Body is wrapped in the standard Mahra branded email template on send.</p>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1">To (phone)</label>
                <input value={toPhone} onChange={e => handleToPhoneChange(e.target.value)} placeholder="+61400000000"
                  className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] font-mono" data-testid="compose-to-phone" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1">Recipient name (optional)</label>
                <input value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="Olivia"
                  className="w-full border border-[#282828]/12 px-3 py-2 text-[13px]" data-testid="compose-recipient-name" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1">
                  Message
                  <span className="ml-2 normal-case text-[#676664]/50 tracking-normal">Tokens: [Customer Name], [Contact Number]</span>
                </label>
                <textarea rows={5} value={body} onChange={e => setBody(e.target.value)} placeholder="Type your message…"
                  className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] resize-y font-mono" data-testid="compose-body" />
                <p className="text-[10px] text-[#676664]/50 mt-1">{body.length}/160 chars — longer messages send as multi-part SMS.</p>
              </div>
            </>
          )}
        </div>

        </div>{/* /scrollable body */}

        {/* Sticky action bar — always visible above keyboard */}
        <div
          className="sticky bottom-0 z-10 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-4 sm:px-5 py-3 bg-white border-t border-[#282828]/10"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <button onClick={onClose} className="w-full sm:w-auto px-4 py-3 text-[11px] uppercase tracking-wider border border-[#282828]/12 text-[#676664]" data-testid="compose-cancel">Cancel</button>
          <button onClick={send} disabled={sending} data-testid="compose-send"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 text-[11px] uppercase tracking-wider bg-[#9A7B4F] text-white hover:bg-[#8B6B3F] disabled:opacity-50">
            {sending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {sending ? "Sending…" : `Send ${isEmail ? "Email" : "SMS"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
